import React, { useEffect, useMemo, useState } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiService } from '../services/apiService';
import { useToast } from '../components/Toast';
import SetupGuard from '../components/SetupGuard';

const defaultDate = new Date().toISOString().slice(0, 10);

const Attendance = () => {
  const { toast } = useToast();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedDate, setSelectedDate] = useState(defaultDate);
  const [students, setStudents] = useState([]);
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadClasses = async () => {
      try {
        setLoading(true);
        setError('');
        const classData = await apiService.getAll('classes');
        setClasses(classData || []);
        if (classData?.length) {
          setSelectedClassId(String(classData[0]._id));
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load classes.');
      } finally {
        setLoading(false);
      }
    };

    loadClasses();
  }, []);

  useEffect(() => {
    const loadAttendance = async () => {
      if (!selectedClassId) return;
      try {
        setLoading(true);
        setError('');
        const [classDetails, attendanceData] = await Promise.all([
          apiService.getById('classes', selectedClassId),
          apiService.get(`attendance/class/${selectedClassId}/${selectedDate}`),
        ]);

        const classStudents = classDetails.students || [];
        const attendanceByStudent = (attendanceData || []).reduce((accumulator, item) => {
          accumulator[item.studentId] = item;
          return accumulator;
        }, {});

        setStudents(classStudents);
        setRecords(
          classStudents.reduce((accumulator, student) => {
            const record = attendanceByStudent[student._id];
            accumulator[student._id] = {
              status: record?.status || 'present',
              remark: record?.remark || '',
            };
            return accumulator;
          }, {})
        );
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load attendance.');
      } finally {
        setLoading(false);
      }
    };

    loadAttendance();
  }, [selectedClassId, selectedDate]);

  const attendanceSummary = useMemo(() => {
    const values = Object.values(records);
    return {
      present: values.filter(item => item.status === 'present').length,
      absent: values.filter(item => item.status === 'absent').length,
      late: values.filter(item => item.status === 'late').length,
    };
  }, [records]);

  const updateRecord = (studentId, field, value) => {
    setRecords(previous => ({
      ...previous,
      [studentId]: {
        ...previous[studentId],
        [field]: value,
      },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await apiService.post('attendance/bulk', {
        classId: Number(selectedClassId),
        date: selectedDate,
        records: students.map(student => ({
          studentId: Number(student._id),
          status: records[student._id]?.status || 'present',
          remark: records[student._id]?.remark || '',
        })),
      });
      setSuccess('Attendance saved successfully.');
      toast('Attendance saved successfully.', 'success');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save attendance.');
      toast(err.response?.data?.message || 'Failed to save attendance.', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !classes.length) return <LoadingSpinner message="Loading attendance..." />;

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Attendance</h1>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !selectedClassId}>
          {saving ? 'Saving...' : 'Save Attendance'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div style={{ background: '#d4edda', color: '#155724', padding: '0.75rem 1rem', borderRadius: 6, marginBottom: '1rem' }}>{success}</div>}

      <SetupGuard checks={[
        {
          ok: classes.length > 0,
          icon: '🏫',
          label: 'No Classes exist yet',
          description: 'Create a class or batch under a programme before taking attendance.',
          action: { label: 'Create Class', to: '/programmes' },
        },
      ]}>

      <div className="form-row" style={{ marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label>Class</label>
          <select value={selectedClassId} onChange={event => setSelectedClassId(event.target.value)}>
            {classes.map(item => (
              <option key={item._id} value={item._id}>{item.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Date</label>
          <input type="date" value={selectedDate} onChange={event => setSelectedDate(event.target.value)} />
        </div>
      </div>

      <div className="stats-grid" style={{ marginBottom: '1.5rem' }}>
        {[
          { label: 'Present', value: attendanceSummary.present, color: '#388E3C' },
          { label: 'Absent', value: attendanceSummary.absent, color: '#D32F2F' },
          { label: 'Late', value: attendanceSummary.late, color: '#F57C00' },
        ].map(item => (
          <div key={item.label} className="stat-card">
            <h3>{item.label}</h3>
            <div className="stat-value" style={{ color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {students.length > 0 && (
        <div style={{ marginBottom: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-light)', alignSelf: 'center', marginRight: '0.25rem' }}>Mark all:</span>
          {['present', 'absent', 'late'].map(s => (
            <button
              key={s}
              type="button"
              className="btn btn-secondary"
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.85rem',
                background: s === 'present' ? '#e8f5e9' : s === 'absent' ? '#fdecea' : '#fff8e1',
                color:      s === 'present' ? '#2e7d32' : s === 'absent' ? '#c62828' : '#e65100',
                border:     `1px solid ${s === 'present' ? '#a5d6a7' : s === 'absent' ? '#ef9a9a' : '#ffcc80'}`,
              }}
              onClick={() => setRecords(prev => {
                const next = { ...prev };
                students.forEach(st => { next[st._id] = { ...next[st._id], status: s }; });
                return next;
              })}
            >
              All {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div className="table-responsive">
        <table className="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Learner</th>
              <th style={{ textAlign: 'center', color: '#2e7d32' }}>Present</th>
              <th style={{ textAlign: 'center', color: '#c62828' }}>Absent</th>
              <th style={{ textAlign: 'center', color: '#e65100' }}>Late</th>
              <th>Remark</th>
            </tr>
          </thead>
          <tbody>
            {students.length === 0 ? (
              <tr><td colSpan={6} className="no-data-message">No learners found in this class.</td></tr>
            ) : students.map((student, index) => {
              const status = records[student._id]?.status || 'present';
              const rowBg  = status === 'present' ? '#f0faf0' : status === 'absent' ? '#fff5f5' : '#fffbf0';
              return (
                <tr key={student._id} style={{ background: rowBg }}>
                  <td style={{ color: 'var(--text-light)', fontSize: '0.82rem', width: 36 }}>{index + 1}</td>
                  <td><strong>{student.name}</strong></td>
                  {['present', 'absent', 'late'].map(s => (
                    <td key={s} style={{ textAlign: 'center' }}>
                      <input
                        type="radio"
                        name={`status-${student._id}`}
                        value={s}
                        checked={status === s}
                        onChange={() => updateRecord(student._id, 'status', s)}
                        style={{ width: 18, height: 18, cursor: 'pointer', accentColor:
                          s === 'present' ? '#388E3C' : s === 'absent' ? '#D32F2F' : '#F57C00'
                        }}
                      />
                    </td>
                  ))}
                  <td>
                    <input
                      value={records[student._id]?.remark || ''}
                      onChange={e => updateRecord(student._id, 'remark', e.target.value)}
                      placeholder="Optional remark"
                      style={{ width: '100%', minWidth: 120 }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </SetupGuard>
    </div>
  );
};

export default Attendance;
